import React, { useState, useEffect } from 'react';
import { 
  notificationStatsService, 
  NotificationStats, 
  NotificationTicket, 
  InvalidToken, 
  DailyStat, 
  ErrorStat 
} from '../../services/notificationStatsService';
const styles = require('./NotificationStatsScreen.module.css').default;

export const NotificationStatsScreen: React.FC = () => {
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [allTicketsForChart, setAllTicketsForChart] = useState<NotificationTicket[]>([]);
  const [invalidTokens, setInvalidTokens] = useState<InvalidToken[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStat[]>([]);
  const [errorStats, setErrorStats] = useState<ErrorStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [pageSize, setPageSize] = useState(5);
  const [totalInvalidTokens, setTotalInvalidTokens] = useState(0);
  const [activeTab, setActiveTab] = useState<'overview' | 'tokens'>('overview');
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; value: number; time: Date; tickets: any[]; type?: string } | null>(null);
  const [showTotalLine, setShowTotalLine] = useState(true);
  const [showSuccessLine, setShowSuccessLine] = useState(true);
  const [showErrorLine, setShowErrorLine] = useState(true);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadAllTicketsForChart(),
        loadInvalidTokens(),
        loadDailyStats(),
        loadErrorStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Error al cargar los datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await notificationStatsService.getStats(days);
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadAllTicketsForChart = async () => {
    try {
      // Cargar TODOS los tickets del período para el gráfico
      // Usar un per_page muy grande para obtener todos los datos
      const data = await notificationStatsService.getTickets({
        days,
        page: 1,
        per_page: 1000 // Ajustar según el máximo esperado
      });
      setAllTicketsForChart(data.data);
      
      // Si hay más de 1000, hacer peticiones adicionales
      if (data.total > 1000) {
        const totalPages = Math.ceil(data.total / 1000);
        const additionalRequests = [];
        
        for (let page = 2; page <= totalPages; page++) {
          additionalRequests.push(
            notificationStatsService.getTickets({
              days,
              page,
              per_page: 1000
            })
          );
        }
        
        const additionalResults = await Promise.all(additionalRequests);
        const allTickets = [...data.data];
        
        additionalResults.forEach(result => {
          allTickets.push(...result.data);
        });
        
        setAllTicketsForChart(allTickets);
      }
    } catch (error) {
      console.error('Error loading all tickets for chart:', error);
    }
  };

  const loadInvalidTokens = async () => {
    try {
      const data = await notificationStatsService.getInvalidTokens({
        days: 30,
        per_page: pageSize
      });
      setInvalidTokens(data.data);
      setTotalInvalidTokens(data.total);
    } catch (error) {
      console.error('Error loading invalid tokens:', error);
    }
  };

  const loadDailyStats = async () => {
    try {
      const data = await notificationStatsService.getDailyStats(days);
      setDailyStats(data);
    } catch (error) {
      console.error('Error loading daily stats:', error);
    }
  };

  const loadErrorStats = async () => {
    try {
      const data = await notificationStatsService.getErrorStats(days);
      setErrorStats(data);
    } catch (error) {
      console.error('Error loading error stats:', error);
    }
  };

  const processReceipts = async () => {
    setLoading(true);
    try {
      const result = await notificationStatsService.processReceipts(100);
      showNotification(`Se procesaron ${result.processed} receipts`, 'success');
      loadData();
    } catch (error) {
      showNotification('Error al procesar receipts', 'error');
      console.error('Error processing receipts:', error);
    } finally {
      setLoading(false);
    }
  };

  const cleanupInvalidTokens = async () => {
    if (window.confirm('¿Confirmar limpieza de tokens inválidos? Esto marcará como removidos los tokens inválidos detectados hace más de 7 días.')) {
      try {
        const result = await notificationStatsService.cleanupInvalidTokens(7);
        showNotification(`Se limpiaron ${result.tokens_cleaned} tokens`, 'success');
        loadData();
      } catch (error) {
        showNotification('Error al limpiar tokens', 'error');
        console.error('Error cleaning up tokens:', error);
      }
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    // Simple notification system
    const notification = document.createElement('div');
    notification.className = `${styles.notification} ${styles[type]}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add(styles.show);
    }, 100);
    
    setTimeout(() => {
      notification.classList.remove(styles.show);
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok': return '#10b981';
      case 'error': return '#ef4444';
      case 'pending': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES');
  };

  const getSuccessRate = () => {
    if (!stats || stats.tickets.total === 0) return 0;
    return Math.round((stats.tickets.successful / stats.tickets.total) * 100);
  };

  const getFailureRate = () => {
    if (!stats || stats.tickets.total === 0) return 0;
    return Math.round((stats.tickets.failed / stats.tickets.total) * 100);
  };

  const getPendingRate = () => {
    if (!stats || stats.tickets.total === 0) return 0;
    return Math.round((stats.tickets.pending / stats.tickets.total) * 100);
  };

  const renderPieChart = () => {
    if (!stats || stats.tickets.total === 0) return null;

    const successRate = getSuccessRate();
    const failureRate = getFailureRate();
    const pendingRate = getPendingRate();

    const data = [
      { label: 'Exitosas', value: stats.tickets.successful, percentage: successRate, color: '#10b981' },
      { label: 'Fallidas', value: stats.tickets.failed, percentage: failureRate, color: '#ef4444' },
      { label: 'Pendientes', value: stats.tickets.pending, percentage: pendingRate, color: '#f59e0b' }
    ].filter(item => item.value > 0);

    const radius = 100; // Aumentado de 70 a 100
    const centerX = 120; // Aumentado de 90 a 120
    const centerY = 120; // Aumentado de 90 a 120
    
    // Caso especial: si solo hay un tipo de estado (100%)
    if (data.length === 1) {
      return (
        <div className={styles['pie-chart-container']}>
          <svg className={styles['pie-chart']} viewBox="0 0 240 240">
            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill={data[0].color}
              stroke="white"
              strokeWidth="3"
              className={styles['pie-slice']}
            />
            {/* Agregar texto en el centro */}
            <text
              x={centerX}
              y={centerY}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="24"
              fontWeight="bold"
              fill="white"
            >
              {data[0].percentage}%
            </text>
          </svg>
          <div className={styles['pie-legend']}>
            {data.map((item, index) => (
              <div key={index} className={styles['legend-item']}>
                <div 
                  className={styles['legend-color']} 
                  style={{ backgroundColor: item.color }}
                ></div>
                <span>{item.label} ({item.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    let currentAngle = -90; // Start from top
    
    const slices = data.map((item, index) => {
      const sliceAngle = (item.percentage / 100) * 360;
      const startAngle = currentAngle;
      const endAngle = currentAngle + sliceAngle;
      
      // Para evitar problemas con arcos de 360 grados, limitamos a 359.9
      const actualSliceAngle = sliceAngle >= 360 ? 359.9 : sliceAngle;
      const actualEndAngle = startAngle + actualSliceAngle;
      
      const x1 = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
      const y1 = centerY + radius * Math.sin((startAngle * Math.PI) / 180);
      const x2 = centerX + radius * Math.cos((actualEndAngle * Math.PI) / 180);
      const y2 = centerY + radius * Math.sin((actualEndAngle * Math.PI) / 180);
      
      const largeArcFlag = actualSliceAngle > 180 ? 1 : 0;
      
      const pathData = [
        `M ${centerX} ${centerY}`,
        `L ${x1} ${y1}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
        'Z'
      ].join(' ');
      
      currentAngle += sliceAngle;
      
      // Calcular posición para el texto del porcentaje
      const midAngle = startAngle + (actualSliceAngle / 2);
      const textRadius = radius * 0.7; // 70% del radio para posicionar el texto
      const textX = centerX + textRadius * Math.cos((midAngle * Math.PI) / 180);
      const textY = centerY + textRadius * Math.sin((midAngle * Math.PI) / 180);
      
      return {
        ...item,
        pathData,
        startAngle,
        endAngle: actualEndAngle,
        textX,
        textY,
        midAngle
      };
    });

    return (
      <div className={styles['pie-chart-container']}>
        <svg className={styles['pie-chart']} viewBox="0 0 240 240">
          {slices.map((slice, index) => (
            <g key={index}>
              <path
                d={slice.pathData}
                fill={slice.color}
                stroke="white"
                strokeWidth="3"
                className={styles['pie-slice']}
              />
              {/* Mostrar porcentaje solo si es mayor a 5% para evitar solapamiento */}
              {slice.percentage >= 5 && (
                <text
                  x={slice.textX}
                  y={slice.textY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="14"
                  fontWeight="bold"
                  fill="white"
                >
                  {slice.percentage}%
                </text>
              )}
            </g>
          ))}
        </svg>
        <div className={styles['pie-legend']}>
          {data.map((item, index) => (
            <div key={index} className={styles['legend-item']}>
              <div 
                className={styles['legend-color']} 
                style={{ backgroundColor: item.color }}
              ></div>
              <span>{item.label} ({item.percentage}%)</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLineChart = () => {
    // Usar allTicketsForChart en lugar de tickets
    if (allTicketsForChart.length === 0) return null;

    const chartHeight = 350;
    const chartWidth = 800;
    const paddingTop = 40;
    const paddingBottom = 80; // Aumentar espacio para las etiquetas rotadas
    const paddingLeft = 60;
    const paddingRight = 40;

    // Obtener el rango de tiempo según el período seleccionado
    const now = new Date();
    const startTime = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));

    // Filtrar tickets dentro del período y ordenar por tiempo
    const periodTickets = allTicketsForChart
      .filter(ticket => new Date(ticket.sent_at) >= startTime)
      .sort((a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime());

    if (periodTickets.length === 0) return null;

    // Para visualización por hora, agrupar por intervalos de 2 minutos
    // Para otros períodos, mostrar puntos individuales o agrupar según corresponda
    let dataPoints: { time: Date; count: number; successCount: number; errorCount: number; tickets: any[] }[] = [];

    if (days === 0.04) { // Última hora - agrupar por intervalos de 2 minutos
      const intervalMinutes = 2;
      dataPoints = [];
      
      // Usar el tiempo actual redondeado al siguiente intervalo de 2 minutos
      const currentTime = new Date();
      currentTime.setSeconds(0, 0); // Quitar segundos y milisegundos
      
      // Redondear hacia arriba al siguiente intervalo de 2 minutos
      const currentMinutes = currentTime.getMinutes();
      const nextIntervalMinutes = Math.ceil(currentMinutes / intervalMinutes) * intervalMinutes;
      currentTime.setMinutes(nextIntervalMinutes);
      
      // Crear 31 intervalos de 2 minutos hacia atrás (para cubrir 60 minutos completos)
      for (let i = 30; i >= 0; i--) { // 31 intervalos para asegurar cobertura completa
        const intervalTime = new Date(currentTime.getTime() - (i * intervalMinutes * 60 * 1000));
        dataPoints.push({ 
          time: intervalTime, 
          count: 0,
          successCount: 0,
          errorCount: 0,
          tickets: [] 
        });
      }
      
      // Asignar tickets a sus intervalos correspondientes
      periodTickets.forEach(ticket => {
        const ticketTime = new Date(ticket.sent_at);
        
        // Encontrar el intervalo correspondiente
        for (let i = 0; i < dataPoints.length; i++) {
          const intervalStart = dataPoints[i].time.getTime();
          const intervalEnd = intervalStart + (intervalMinutes * 60 * 1000);
          
          if (ticketTime.getTime() >= intervalStart && ticketTime.getTime() < intervalEnd) {
            dataPoints[i].count++;
            dataPoints[i].tickets.push(ticket);
            
            // Contar por estado
            if (ticket.status === 'ok') {
              dataPoints[i].successCount++;
            } else if (ticket.status === 'error') {
              dataPoints[i].errorCount++;
            }
            break;
          }
        }
      });
    } else {
      // Para otros períodos, mostrar cada notificación individual
      // pero agrupar las que ocurren en el mismo minuto
      const groupedByMinute = new Map<string, { time: Date; count: number; successCount: number; errorCount: number; tickets: any[] }>();
      
      periodTickets.forEach(ticket => {
        const ticketTime = new Date(ticket.sent_at);
        const key = ticketTime.toISOString().substring(0, 16); // YYYY-MM-DDTHH:mm
        
        if (!groupedByMinute.has(key)) {
          groupedByMinute.set(key, { time: ticketTime, count: 0, successCount: 0, errorCount: 0, tickets: [] });
        }
        
        const group = groupedByMinute.get(key)!;
        group.count++;
        group.tickets.push(ticket);
        
        // Contar por estado
        if (ticket.status === 'ok') {
          group.successCount++;
        } else if (ticket.status === 'error') {
          group.errorCount++;
        }
      });

      dataPoints = Array.from(groupedByMinute.values())
        .sort((a, b) => a.time.getTime() - b.time.getTime());
    }

    // Debug: Log para verificar agrupación
    console.log('Total tickets en período:', periodTickets.length);
    console.log('Puntos de datos agrupados:', dataPoints.length);
    console.log('Suma de notificaciones en puntos:', dataPoints.reduce((sum, dp) => sum + dp.count, 0));

    // Validación adicional: asegurar que tenemos datos válidos
    if (dataPoints.length === 0) {
      return (
        <div className={styles['line-chart-container']}>
          <div className={styles['no-data-message']}>
            <p>No hay datos disponibles para el período seleccionado</p>
          </div>
        </div>
      );
    }

    const maxValue = Math.max(...dataPoints.map(d => Math.max(d.count, d.successCount, d.errorCount)), 1);

    // Función para formatear las etiquetas del eje X
    const formatXAxisLabel = (date: Date, days: number) => {
      if (days === 0.04) {
        // Última hora - mostrar hora:minuto
        return date.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      } else if (days === 0.5) {
        // Últimas 12 horas
        return date.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      } else if (days === 1) {
        // Último día
        return date.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      } else if (days <= 3) {
        // Últimos 3 días
        return date.toLocaleDateString('es-ES', { 
          day: 'numeric'
        }) + ' ' + date.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
      } else {
        // Más de 3 días
        return date.toLocaleDateString('es-ES', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    };

    // Función para obtener el título del período
    const getPeriodTitle = () => {
      if (days === 0.04) {
        const endTime = new Date();
        endTime.setSeconds(0, 0);
        const currentMinutes = endTime.getMinutes();
        const roundedMinutes = Math.floor(currentMinutes / 2) * 2;
        endTime.setMinutes(roundedMinutes);
        
        const startTimeFormatted = new Date(endTime.getTime() - (60 * 60 * 1000));
        
        return `${startTimeFormatted.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false })} - ${endTime.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false })}`;
      } else if (days === 0.5) {
        return 'Últimas 12 Horas';
      } else if (days === 1) {
        return 'Último Día';
      } else if (days === 3) {
        return 'Últimos 3 Días';
      } else if (days === 7) {
        return 'Últimos 7 Días';
      } else if (days === 30) {
        return 'Últimos 30 Días';
      } else {
        return `Últimos ${days} Días`;
      }
    };

    // Calcular el ancho real del gráfico
    const graphWidth = chartWidth - paddingLeft - paddingRight;
    const graphHeight = chartHeight - paddingTop - paddingBottom;

    // Generar puntos para las líneas
    const totalPoints = dataPoints.map((data, index) => {
      const xRatio = dataPoints.length > 1 ? (index / (dataPoints.length - 1)) : 0.5;
      const x = paddingLeft + xRatio * graphWidth;
      const yRatio = maxValue > 0 ? (data.count / maxValue) : 0;
      const y = chartHeight - paddingBottom - (yRatio * graphHeight);
      return { x, y, value: data.count, time: data.time, tickets: data.tickets, type: 'total' };
    });

    const successPoints = dataPoints.map((data, index) => {
      const xRatio = dataPoints.length > 1 ? (index / (dataPoints.length - 1)) : 0.5;
      const x = paddingLeft + xRatio * graphWidth;
      const yRatio = maxValue > 0 ? (data.successCount / maxValue) : 0;
      const y = chartHeight - paddingBottom - (yRatio * graphHeight);
      return { x, y, value: data.successCount, time: data.time, tickets: data.tickets.filter(t => t.status === 'ok'), type: 'success' };
    });

    const errorPoints = dataPoints.map((data, index) => {
      const xRatio = dataPoints.length > 1 ? (index / (dataPoints.length - 1)) : 0.5;
      const x = paddingLeft + xRatio * graphWidth;
      const yRatio = maxValue > 0 ? (data.errorCount / maxValue) : 0;
      const y = chartHeight - paddingBottom - (yRatio * graphHeight);
      return { x, y, value: data.errorCount, time: data.time, tickets: data.tickets.filter(t => t.status === 'error'), type: 'error' };
    });

    // Si no hay puntos con datos, mostrar líneas planas en 0
    if (totalPoints.every(p => p.value === 0)) {
      totalPoints.forEach(p => {
        p.y = chartHeight - paddingBottom;
      });
    }
    if (successPoints.every(p => p.value === 0)) {
      successPoints.forEach(p => {
        p.y = chartHeight - paddingBottom;
      });
    }
    if (errorPoints.every(p => p.value === 0)) {
      errorPoints.forEach(p => {
        p.y = chartHeight - paddingBottom;
      });
    }

    const totalPathData = totalPoints.map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ');

    const successPathData = successPoints.map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ');

    const errorPathData = errorPoints.map((point, index) => {
      return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }).join(' ');

    // Mostrar etiquetas cada ciertos intervalos para evitar solapamiento
    const showLabelEvery = days === 0.04 ? 1 : Math.max(1, Math.floor(dataPoints.length / 8));

    return (
      <div className={styles['line-chart-container']} style={{ position: 'relative' }}>
        <div className={styles['chart-period-info']}>
          <span className={styles['period-badge']}>{getPeriodTitle()}</span>
          <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6b7280' }}>
            Total: {dataPoints.reduce((sum, dp) => sum + dp.count, 0)} notificaciones
          </span>
        </div>
        
        <svg className={styles['line-chart']} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="totalGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#60a5fa"/>
              <stop offset="50%" stopColor="#3b82f6"/>
              <stop offset="100%" stopColor="#1d4ed8"/>
            </linearGradient>
            <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#86efac"/>
              <stop offset="50%" stopColor="#10b981"/>
              <stop offset="100%" stopColor="#059669"/>
            </linearGradient>
            <linearGradient id="errorGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#fca5a5"/>
              <stop offset="50%" stopColor="#ef4444"/>
              <stop offset="100%" stopColor="#dc2626"/>
            </linearGradient>
            <linearGradient id="totalAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05"/>
            </linearGradient>
            <linearGradient id="successAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#10b981" stopOpacity="0.05"/>
            </linearGradient>
            <linearGradient id="errorAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.05"/>
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
              <feMerge> 
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>
          
          {/* Grid lines horizontales */}
          {(() => {
            const yLabels = [];
            
            // Si maxValue es 0, mostrar solo 0
            if (maxValue === 0) {
              yLabels.push(0);
            } else {
              const step = maxValue > 5 ? Math.ceil(maxValue / 5) : 1;
              
              for (let i = 0; i <= maxValue; i += step) {
                yLabels.push(i);
              }
              
              if (yLabels[yLabels.length - 1] < maxValue) {
                yLabels.push(maxValue);
              }
              
              if (yLabels.length > 6) {
                const newStep = Math.ceil(maxValue / 5);
                yLabels.length = 0;
                for (let i = 0; i <= maxValue; i += newStep) {
                  yLabels.push(i);
                }
              }
            }
            
            return yLabels.reverse().map((value, index) => {
              // Evitar división por cero
              const yRatio = yLabels.length > 1 ? (index / (yLabels.length - 1)) : 0;
              const y = paddingTop + yRatio * graphHeight;
              return (
                <line
                  key={`grid-${value}`}
                  x1={paddingLeft}
                  y1={y}
                  x2={chartWidth - paddingRight}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeWidth="1"
                  strokeDasharray="3,3"
                />
              );
            });
          })()}
          
          {/* Grid lines verticales */}
          {successPoints.filter((_, index) => index % showLabelEvery === 0).map((point, index) => (
            <line
              key={`v-${index}`}
              x1={point.x}
              y1={paddingTop}
              x2={point.x}
              y2={chartHeight - paddingBottom}
              stroke="#f3f4f6"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
          ))}
          
          {/* Y-axis labels */}
          {(() => {
            // Calcular etiquetas únicas para el eje Y
            const yLabels = [];
            
            // Si maxValue es 0, mostrar solo 0
            if (maxValue === 0) {
              yLabels.push(0);
            } else {
              const step = maxValue > 5 ? Math.ceil(maxValue / 5) : 1;
              
              for (let i = 0; i <= maxValue; i += step) {
                yLabels.push(i);
              }
              
              // Asegurar que el valor máximo esté incluido
              if (yLabels[yLabels.length - 1] < maxValue) {
                yLabels.push(maxValue);
              }
              
              // Limitar a 6 etiquetas máximo
              if (yLabels.length > 6) {
                const newStep = Math.ceil(maxValue / 5);
                yLabels.length = 0;
                for (let i = 0; i <= maxValue; i += newStep) {
                  yLabels.push(i);
                }
              }
            }
            
            return yLabels.reverse().map((value, index) => {
              // Evitar división por cero
              const yRatio = yLabels.length > 1 ? (index / (yLabels.length - 1)) : 0;
              const y = paddingTop + yRatio * graphHeight;
              return (
                <text
                  key={`y-${value}`}
                  x={paddingLeft - 10}
                  y={y + 5}
                  textAnchor="end"
                  fontSize="12"
                  fill="#6b7280"
                  fontWeight="500"
                >
                  {value}
                </text>
              );
            });
          })()}
          
          {/* Areas under the lines */}
          {showTotalLine && (
            <path
              d={`${totalPathData} L ${totalPoints[totalPoints.length - 1].x} ${chartHeight - paddingBottom} L ${totalPoints[0].x} ${chartHeight - paddingBottom} Z`}
              fill="url(#totalAreaGradient)"
              className={styles['line-area']}
            />
          )}
          {showSuccessLine && (
            <path
              d={`${successPathData} L ${successPoints[successPoints.length - 1].x} ${chartHeight - paddingBottom} L ${successPoints[0].x} ${chartHeight - paddingBottom} Z`}
              fill="url(#successAreaGradient)"
              className={styles['line-area']}
            />
          )}
          {showErrorLine && (
            <path
              d={`${errorPathData} L ${errorPoints[errorPoints.length - 1].x} ${chartHeight - paddingBottom} L ${errorPoints[0].x} ${chartHeight - paddingBottom} Z`}
              fill="url(#errorAreaGradient)"
              className={styles['line-area']}
            />
          )}
          
          {/* Main lines */}
          {showTotalLine && (
            <path
              d={totalPathData}
              fill="none"
              stroke="url(#totalGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
              className={styles['trend-line']}
            />
          )}
          {showSuccessLine && (
            <path
              d={successPathData}
              fill="none"
              stroke="url(#successGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
              className={styles['trend-line']}
            />
          )}
          {showErrorLine && (
            <path
              d={errorPathData}
              fill="none"
              stroke="url(#errorGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
              className={styles['trend-line']}
            />
          )}
          
          {/* Data points and X-axis labels */}
          {totalPoints.map((point, index) => {
            const dataPoint = dataPoints[index];
            const successPoint = successPoints[index];
            const errorPoint = errorPoints[index];
            const isHovered = hoveredPoint && hoveredPoint.time.getTime() === point.time.getTime();
            
            return (
              <g key={index}>
                {/* Área invisible para detectar hover */}
                <rect
                  x={point.x - 15}
                  y={paddingTop}
                  width={30}
                  height={graphHeight}
                  fill="transparent"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => {
                    // Por defecto mostrar el total si está visible, sino la exitosa, sino la fallida
                    if (showTotalLine && point.value > 0) {
                      setHoveredPoint(point);
                    } else if (showSuccessLine && successPoint.value > 0) {
                      setHoveredPoint(successPoint);
                    } else if (showErrorLine && errorPoint.value > 0) {
                      setHoveredPoint(errorPoint);
                    }
                  }}
                  onMouseLeave={() => setHoveredPoint(null)}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    const relativeY = (y / rect.height) * graphHeight + paddingTop;
                    
                    // Encontrar el punto más cercano al cursor
                    const points = [];
                    if (showTotalLine && point.value > 0) {
                      points.push({ p: point, dist: Math.abs(point.y - relativeY) });
                    }
                    if (showSuccessLine && successPoint.value > 0) {
                      points.push({ p: successPoint, dist: Math.abs(successPoint.y - relativeY) });
                    }
                    if (showErrorLine && errorPoint.value > 0) {
                      points.push({ p: errorPoint, dist: Math.abs(errorPoint.y - relativeY) });
                    }
                    
                    if (points.length > 0) {
                      points.sort((a, b) => a.dist - b.dist);
                      setHoveredPoint(points[0].p);
                    }
                  }}
                />
                
                {/* Mostrar punto azul para total solo en hover */}
                {showTotalLine && point.value > 0 && isHovered && hoveredPoint?.type === 'total' && (
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="6"
                    fill="#3b82f6"
                    stroke="white"
                    strokeWidth="2.5"
                    className={styles['data-point']}
                    style={{ pointerEvents: 'none', animation: 'fadeIn 0.2s ease-out' }}
                  />
                )}
                
                {/* Mostrar punto verde para exitosas solo en hover */}
                {showSuccessLine && successPoint.value > 0 && isHovered && hoveredPoint?.type === 'success' && (
                  <circle
                    cx={successPoint.x}
                    cy={successPoint.y}
                    r="6"
                    fill="#10b981"
                    stroke="white"
                    strokeWidth="2.5"
                    className={styles['data-point']}
                    style={{ pointerEvents: 'none', animation: 'fadeIn 0.2s ease-out' }}
                  />
                )}
                
                {/* Mostrar punto rojo para fallidas solo en hover */}
                {showErrorLine && errorPoint.value > 0 && isHovered && hoveredPoint?.type === 'error' && (
                  <circle
                    cx={errorPoint.x}
                    cy={errorPoint.y}
                    r="6"
                    fill="#ef4444"
                    stroke="white"
                    strokeWidth="2.5"
                    className={styles['data-point']}
                    style={{ pointerEvents: 'none', animation: 'fadeIn 0.2s ease-out' }}
                  />
                )}
                
                {/* X-axis labels - mostrar TODAS para última hora */}
                {(days === 0.04 || index % showLabelEvery === 0) && (
                  <text
                    x={point.x}
                    y={chartHeight - paddingBottom + 20}
                    textAnchor={days === 0.04 ? "end" : "middle"}
                    fontSize="10"
                    fill="#6b7280"
                    fontWeight="500"
                    transform={days === 0.04 ? `rotate(-90 ${point.x} ${chartHeight - paddingBottom + 20})` : ''}
                  >
                    {formatXAxisLabel(dataPoint.time, days)}
                  </text>
                )}
              </g>
            );
          })}
          
          {/* Axis lines */}
          <line
            x1={paddingLeft}
            y1={chartHeight - paddingBottom}
            x2={chartWidth - paddingRight}
            y2={chartHeight - paddingBottom}
            stroke="#374151"
            strokeWidth="2"
          />
          <line
            x1={paddingLeft}
            y1={paddingTop}
            x2={paddingLeft}
            y2={chartHeight - paddingBottom}
            stroke="#374151"
            strokeWidth="2"
          />
          
          {/* Axis labels */}
          <text
            x={paddingLeft - 45}
            y={chartHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            fontWeight="600"
            transform={`rotate(-90 ${paddingLeft - 45} ${chartHeight / 2})`}
          >
            Notificaciones
          </text>
          
          <text
            x={chartWidth / 2}
            y={chartHeight - 10}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            fontWeight="600"
          >
            {days === 0.04 ? 'Tiempo (intervalos de 2 min)' : 
             days <= 1 ? 'Tiempo' : 
             'Período'}
          </text>
        </svg>
        
        {/* Tooltip */}
        {hoveredPoint && hoveredPoint.value > 0 && (
          <div 
            className={styles['chart-tooltip']}
            style={{
              position: 'absolute',
              left: `${(hoveredPoint.x / chartWidth) * 100}%`,
              top: `${(hoveredPoint.y / chartHeight) * 100}%`,
              transform: 'translate(-50%, -100%) translateY(-20px)',
              backgroundColor: 'rgba(17, 24, 39, 0.95)',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              pointerEvents: 'none',
              zIndex: 10
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              {hoveredPoint.time.toLocaleDateString('es-ES', { 
                day: 'numeric', 
                month: 'short',
                year: 'numeric'
              })}, {hoveredPoint.time.toLocaleTimeString('es-ES', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ 
                color: hoveredPoint.type === 'success' ? '#10b981' : hoveredPoint.type === 'error' ? '#ef4444' : '#3b82f6', 
                fontSize: '16px' 
              }}>
                {hoveredPoint.value}
              </span>
              <span style={{ color: '#9ca3af' }}>
                {hoveredPoint.value === 1 ? 'notificación ' : 'notificaciones '} 
                {hoveredPoint.type === 'success' ? 'exitosas' : hoveredPoint.type === 'error' ? 'fallidas' : 'totales'}
              </span>
            </div>
          </div>
        )}
        
        {/* Leyenda */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '13px', color: '#374151' }}>
            Mostrar líneas:
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={showTotalLine} 
              onChange={(e) => setShowTotalLine(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '3px', backgroundColor: '#3b82f6', borderRadius: '2px' }}></div>
              <span>Total</span>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={showSuccessLine} 
              onChange={(e) => setShowSuccessLine(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '3px', backgroundColor: '#10b981', borderRadius: '2px' }}></div>
              <span>Exitosas</span>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={showErrorLine} 
              onChange={(e) => setShowErrorLine(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '12px', height: '3px', backgroundColor: '#ef4444', borderRadius: '2px' }}></div>
              <span>Fallidas</span>
            </div>
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['notification-stats-screen']}>
      {/* Header */}
      <div className={styles['stats-header']}>
        <div className={styles['header-title']}>
          <h1>📊 Panel de Notificaciones</h1>
          <p>Monitoreo en tiempo real del sistema de notificaciones push</p>
        </div>
        <div className={styles['header-controls']}>
          <select 
            value={days} 
            onChange={(e) => setDays(Number(e.target.value))}
            className={styles['period-select']}
          >
            <option value={0.04}>Última hora</option>
            <option value={0.5}>Últimas 12 horas</option>
            <option value={1}>Último día</option>
            <option value={3}>Últimos 3 días</option>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
          </select>
          <button 
            onClick={loadData} 
            disabled={loading}
            className={`${styles.btn} ${styles['btn-primary']}`}
          >
            {loading ? '🔄 Cargando...' : '🔄 Actualizar'}
          </button>
          <button 
            onClick={processReceipts}
            disabled={loading}
            className={`${styles.btn} ${styles['btn-success']}`}
          >
            ✅ Procesar Receipts
          </button>
          <button 
            onClick={cleanupInvalidTokens}
            className={`${styles.btn} ${styles['btn-danger']}`}
          >
            🗑️ Limpiar Tokens
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles['tab-navigation']}>
        <button 
          className={`${styles['tab-button']} ${activeTab === 'overview' ? styles.active : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Dashboard
        </button>
        <button 
          className={`${styles['tab-button']} ${activeTab === 'tokens' ? styles.active : ''}`}
          onClick={() => setActiveTab('tokens')}
        >
          🚫 Tokens Inválidos ({totalInvalidTokens})
        </button>
      </div>

      {/* Overview Tab - Combined Dashboard */}
      {activeTab === 'overview' && (
        <div className={styles['tab-content']}>
          {/* KPI Cards */}
          <div className={styles['kpi-grid']}>
            <div className={`${styles['kpi-card']} ${styles.primary}`}>
              <div className={styles['kpi-icon']}>📤</div>
              <div className={styles['kpi-content']}>
                <div className={styles['kpi-value']}>{stats?.tickets.total || 0}</div>
                <div className={styles['kpi-label']}>Total Enviadas</div>
                <div className={styles['kpi-trend']}>Últimos {days} días</div>
              </div>
            </div>
            
            <div className={`${styles['kpi-card']} ${styles.success}`}>
              <div className={styles['kpi-icon']}>✅</div>
              <div className={styles['kpi-content']}>
                <div className={styles['kpi-value']}>{stats?.tickets.successful || 0}</div>
                <div className={styles['kpi-label']}>Exitosas</div>
                <div className={styles['kpi-trend']}>{getSuccessRate()}% del total</div>
              </div>
            </div>
            
            <div className={`${styles['kpi-card']} ${styles.error}`}>
              <div className={styles['kpi-icon']}>❌</div>
              <div className={styles['kpi-content']}>
                <div className={styles['kpi-value']}>{stats?.tickets.failed || 0}</div>
                <div className={styles['kpi-label']}>Fallidas</div>
                <div className={styles['kpi-trend']}>{getFailureRate()}% del total</div>
              </div>
            </div>
            
            <div className={`${styles['kpi-card']} ${styles.warning}`}>
              <div className={styles['kpi-icon']}>⏳</div>
              <div className={styles['kpi-content']}>
                <div className={styles['kpi-value']}>{stats?.tickets.pending || 0}</div>
                <div className={styles['kpi-label']}>Pendientes</div>
                <div className={styles['kpi-trend']}>{getPendingRate()}% del total</div>
              </div>
            </div>
            
            <div className={`${styles['kpi-card']} ${styles.info}`}>
              <div className={styles['kpi-icon']}>📱</div>
              <div className={styles['kpi-content']}>
                <div className={styles['kpi-value']}>{stats?.active_tokens || 0}</div>
                <div className={styles['kpi-label']}>Tokens Activos</div>
                <div className={styles['kpi-trend']}>Dispositivos conectados</div>
              </div>
            </div>
            
            <div className={`${styles['kpi-card']} ${styles.danger}`}>
              <div className={styles['kpi-icon']}>🚫</div>
              <div className={styles['kpi-content']}>
                <div className={styles['kpi-value']}>{stats?.invalid_tokens.total || 0}</div>
                <div className={styles['kpi-label']}>Tokens Inválidos</div>
                <div className={styles['kpi-trend']}>Detectados automáticamente</div>
              </div>
            </div>
          </div>

          
          {/* Analytics Section */}
          <div className={styles['analytics-grid']}>
            <div className={styles['chart-card']}>
              <h3>🥧 Distribución de Estados</h3>
              {renderPieChart()}
            </div>
            
            <div className={`${styles['chart-card']} ${styles.wide}`}>
              <h3>📈 Notificaciones por Período</h3>
              {renderLineChart()}
            </div>
          </div>
        </div>
      )}

      {/* Invalid Tokens Tab */}
      {activeTab === 'tokens' && (
        <div className={styles['tab-content']}>
          <div className={styles['table-section']}>
            <div className={styles['table-header']}>
              <h3>🚫 Tokens Inválidos Detectados</h3>
              <p>Dispositivos que han desinstalado la aplicación o tokens expirados</p>
            </div>
            
            <div className={styles['modern-table']}>
              <div className={styles['table-header-row']}>
                <div className={styles['table-cell']}>Usuario</div>
                <div className={styles['table-cell']}>Email</div>
                <div className={styles['table-cell']}>Token</div>
                <div className={styles['table-cell']}>Error</div>
                <div className={styles['table-cell']}>Detectado</div>
                <div className={styles['table-cell']}>Estado</div>
              </div>
              
              {invalidTokens.map((token) => (
                <div key={token.id} className={styles['table-row']}>
                  <div className={styles['table-cell']}>
                    <span className={styles['user-name']}>
                      {token.usuario 
                        ? `${token.usuario.nombre} ${token.usuario.apellido_paterno}`
                        : '👤 Usuario desconocido'
                      }
                    </span>
                  </div>
                  <div className={styles['table-cell']}>
                    <span className={styles['user-email']}>{token.usuario?.correo || '-'}</span>
                  </div>
                  <div className={styles['table-cell']}>
                    <span className={styles['token-preview']} title={token.token}>
                      {token.token.substring(0, 20)}...
                    </span>
                  </div>
                  <div className={styles['table-cell']}>
                    <span className={styles['error-type']}>{token.error_type}</span>
                  </div>
                  <div className={styles['table-cell']}>
                    <span className={styles['date-time']}>{formatDate(token.detected_at)}</span>
                  </div>
                  <div className={styles['table-cell']}>
                    <span className={`${styles['status-badge']} ${token.removed_at ? styles.removed : styles.pending}`}>
                      {token.removed_at ? '✅ Removido' : '⏳ Pendiente'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};